import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CalendarClock } from "lucide-react";
import { AuthenticationService } from "@/api/auth";

const EmailOtpVerification = () => {
  const { postAuthConfirmSignUp, postAuthResendOTP } = AuthenticationService;
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // In a real app, you'd grab the email from location state or a global context
  const email = location.state?.email || "your email";

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);

    try {
      const data = {
        email: email,
        code: otp,
      };
      if (otp) {
        const response: any = await postAuthConfirmSignUp(data);

        if (response.statusCode === 200 || response.statusCode === 201) {
          toast({
            title: "Verification successful",
            description:
              response.body.message ||
              "You've successfully verified your email!",
          });
          navigate("/events");
        } else {
          toast({
            variant: "destructive",
            title: "Account creation failed",
            description:
              response.body.message ||
              "There was a problem creating your account.",
          });
        }
        // toast({
        //   title: "Verification successful",
        //   description: "You've successfully verified your email!",
        // });
        // navigate("/dashboard");
      } else {
        toast({
          variant: "destructive",
          title: "Invalid OTP",
          description: "The code you entered is incorrect. Please try again.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: "Something went wrong. Please try again later.",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    setResendDisabled(true);

    try {
      const response: any = await postAuthResendOTP({ email });

      if (response.statusCode === 200 || response.statusCode === 201) {
        toast({
          title: "OTP resent",
          description:
            response.body.message ||
            "OTP has been successfully resent to your email!",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Resend OTP failed",
          description:
            response.body.message || "There was a problem resending your OTP.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "OTP resend failed",
        description: "Something went wrong. Please try again later.",
      });
    } finally {
      setResendDisabled(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="flex items-center mb-8">
        <CalendarClock className="h-8 w-8 text-cecure-700 mr-2" />
        <span className="text-2xl font-bold text-cecure-900 font-heading">
          CecureStream <span className="text-accent">Events</span>
        </span>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">Verify Email</CardTitle>
          <CardDescription className="text-center">
            Enter the 6-digit code sent to <strong>{email}</strong>
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleVerify}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">OTP Code</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter OTP"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full bg-cecure-700 hover:bg-cecure-800"
              disabled={isVerifying}
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </Button>
            <div className="text-center text-sm">
              Didn't receive the code?{" "}
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendDisabled}
                className="text-cecure-700 hover:text-cecure-900 font-medium"
              >
                {resendDisabled ? " Resending..." : "Resend OTP"}
              </button>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EmailOtpVerification;
